import { createContext, useContext, useEffect, useState } from "react";
import safeStorage from '../utility/safeStorage';

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  // 🔹 Main wishlist data
  // { [bookId]: { count, isWishlisted } }
  const [wishlistMap, setWishlistMap] = useState({});

  // 🔹 Loading state per book (prevent double clicks)
  // { [bookId]: true/false }
  const [loadingMap, setLoadingMap] = useState({});

  // -------------------------------
  // ✅ Update wishlist state
  // -------------------------------
  const updateWishlist = (bookId, data) => {
    setWishlistMap(prev => ({
      ...prev,
      [bookId]: data
    }));
  };

  // -------------------------------
  // ✅ Set loading state per book
  // -------------------------------
  const setLoading = (bookId, value) => {
    setLoadingMap(prev => ({
      ...prev,
      [bookId]: value
    }));
  };

  // -------------------------------
  // ✅ Reset (use on logout)
  // -------------------------------
  const resetWishlist = () => {
    setWishlistMap({});
    setLoadingMap({});
    safeStorage.removeItem("wishlistMap");
  };

  // -------------------------------
  // ✅ Load from localStorage (on app start)
  // -------------------------------
  useEffect(() => {
    const stored = safeStorage.getItem("wishlistMap");

    if (stored) {
      try {
        setWishlistMap(JSON.parse(stored));
      } catch {
        setWishlistMap({});
      }
    }
  }, []);

  // -------------------------------
  // ✅ Save to localStorage (whenever it changes)
  // -------------------------------
  useEffect(() => {
    safeStorage.setItem("wishlistMap", JSON.stringify(wishlistMap));
  }, [wishlistMap]);

  // -------------------------------
  // ✅ Sync across browser tabs
  // -------------------------------
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "wishlistMap") {
        try {
          setWishlistMap(JSON.parse(e.newValue || "{}"));
        } catch {
          setWishlistMap({});
        }
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // -------------------------------
  // 🔹 Provide everything
  // -------------------------------
  return (
    <WishlistContext.Provider
      value={{
        wishlistMap,
        updateWishlist,
        loadingMap,
        setLoading,
        resetWishlist
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

// -------------------------------
// ✅ Hook
// -------------------------------
export const useWishlist = () => {
  const context = useContext(WishlistContext);

  if (!context) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }

  return context;
};